import type {
  CreateProInquiryDto,
  CreateProRefundRequestDto,
  ProAdminRefundApprovalDto,
  ProAdminRefundDecisionDto,
  ProAdminRefundDecisionInput,
  ProAdminRefundDetailDto,
  ProAdminRefundListDto,
  ProAdminRefundListQuery,
  ProInquiryDto,
  ProInquiryListDto,
  ProRefundRequestDto,
  ProRefundRequestListDto,
} from "dutyon-server/types";
import { useAuthStore } from "../auth/authStore";
import { httpClient } from "./httpClient";
import { proSupportRequestConfig } from "./pro-support-session";

export async function getProInquiries(owner: string, cursor?: string): Promise<ProInquiryListDto> {
  return (
    await httpClient.get<ProInquiryListDto>("/pro/inquiries", {
      ...proSupportRequestConfig(useAuthStore.getState(), owner),
      params: cursor === undefined ? undefined : { cursor },
    })
  ).data;
}
export async function createProInquiry(
  owner: string,
  input: CreateProInquiryDto,
  requestKey: string,
): Promise<ProInquiryDto> {
  const config = proSupportRequestConfig(useAuthStore.getState(), owner);
  return (
    await httpClient.post<ProInquiryDto>("/pro/inquiries", input, {
      ...config,
      headers: { ...config.headers, "Idempotency-Key": requestKey },
    })
  ).data;
}

export async function getProInquiry(owner: string, id: string): Promise<ProInquiryDto> {
  return (
    await httpClient.get<ProInquiryDto>(
      `/pro/inquiries/${encodeURIComponent(id)}`,
      proSupportRequestConfig(useAuthStore.getState(), owner),
    )
  ).data;
}

/** Capture the account token and disable cross-account refresh/replay like support submissions. */
export async function createProRefundRequest(
  owner: string,
  paymentId: string,
  input: CreateProRefundRequestDto,
  requestKey: string,
): Promise<ProRefundRequestDto> {
  const config = proSupportRequestConfig(useAuthStore.getState(), owner);
  return (
    await httpClient.post<ProRefundRequestDto>(
      `/pro/billing/payments/${encodeURIComponent(paymentId)}/refund-requests`,
      input,
      { ...config, headers: { ...config.headers, "Idempotency-Key": requestKey } },
    )
  ).data;
}

export async function getProRefundRequests(
  owner: string,
  paymentId: string,
  cursor?: string,
): Promise<ProRefundRequestListDto> {
  return (
    await httpClient.get<ProRefundRequestListDto>(
      `/pro/billing/payments/${encodeURIComponent(paymentId)}/refund-requests`,
      {
        ...proSupportRequestConfig(useAuthStore.getState(), owner),
        params: cursor === undefined ? undefined : { cursor },
      },
    )
  ).data;
}

export async function getProAdminRefundRequests(
  owner: string,
  filters: ProAdminRefundListQuery = {},
): Promise<ProAdminRefundListDto> {
  return (
    await httpClient.get<ProAdminRefundListDto>("/admin/pro/refund-requests", {
      ...proSupportRequestConfig(useAuthStore.getState(), owner),
      params: filters,
    })
  ).data;
}

export async function getProAdminRefundRequest(
  owner: string,
  id: string,
): Promise<ProAdminRefundDetailDto> {
  return (
    await httpClient.get<ProAdminRefundDetailDto>(
      `/admin/pro/refund-requests/${encodeURIComponent(id)}`,
      proSupportRequestConfig(useAuthStore.getState(), owner),
    )
  ).data;
}

export async function rejectProRefundRequest(
  owner: string,
  id: string,
  input: ProAdminRefundDecisionInput,
  requestKey: string,
): Promise<ProAdminRefundDecisionDto> {
  const config = proSupportRequestConfig(useAuthStore.getState(), owner);
  return (
    await httpClient.post<ProAdminRefundDecisionDto>(
      `/admin/pro/refund-requests/${encodeURIComponent(id)}/reject`,
      input,
      { ...config, headers: { ...config.headers, "Idempotency-Key": requestKey } },
    )
  ).data;
}

export async function approveProRefundRequest(
  owner: string,
  id: string,
  input: ProAdminRefundDecisionInput,
  requestKey: string,
): Promise<ProAdminRefundApprovalDto> {
  const config = proSupportRequestConfig(useAuthStore.getState(), owner);
  return (
    await httpClient.post<ProAdminRefundApprovalDto>(
      `/admin/pro/refund-requests/${encodeURIComponent(id)}/approve`,
      input,
      { ...config, headers: { ...config.headers, "Idempotency-Key": requestKey } },
    )
  ).data;
}
